import React, { useState, useEffect } from "react"
import { useSearchParams, useNavigate, Link } from "react-router-dom"
import { Helmet } from "react-helmet"
import NavigationBar from "../../components/ui/NavigationBar"
import Button from "../../components/ui/Button"
import Icon from "../../components/AppIcon"
import { getCourse } from "../../utils/courseHelpers"
import { getOrganization } from "../../utils/orgHelpers"
import { authHelpers } from "../../firebaseClient"
import { createEnrollment, getUserEnrollment, getLearnerIdentity } from "../../utils/enrollmentHelpers"
import { logProductView } from "../../utils/productViewHelpers"
import { logPageView } from "../../utils/pageViewHelpers"

const LESSON_TYPE_ICONS = {
  video: "PlayCircle",
  text: "FileText",
  pdf: "File",
  external: "Link",
  live: "Video",
}

const CoursePreview = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const orgId = searchParams?.get("orgId")
  const courseId = searchParams?.get("courseId")

  const [course, setCourse] = useState(null)
  const [organization, setOrganization] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [enrollment, setEnrollment] = useState(null)
  const [enrolling, setEnrolling] = useState(false)
  const [enrollError, setEnrollError] = useState("")

  useEffect(() => {
    const load = async () => {
      if (!orgId || !courseId) {
        setNotFound(true)
        setLoading(false)
        return
      }

      const { course: courseData } = await getCourse(orgId, courseId)

      if (!courseData || courseData?.status !== "published" || courseData?.visibility !== "public") {
        setNotFound(true)
        setLoading(false)
        return
      }

      const { organization: orgData } = await getOrganization(orgId)

      const { session } = await authHelpers.getSession()
      const loggedInUser = session?.user || null
      setCurrentUser(loggedInUser)

      if (loggedInUser) {
        const { enrollment: existingEnrollment } = await getUserEnrollment(loggedInUser.uid, courseId)
        setEnrollment(existingEnrollment)
      }

            setCourse(courseData)
      setOrganization(orgData)
      setLoading(false)

            // Fire-and-forget — a view counts whether or not the visitor is logged
      // in yet, so this runs after the auth check but doesn't wait on it.
      logProductView({
        orgId,
        productId: courseId,
        productType: courseData?.courseType || "course",
        viewerId: loggedInUser?.uid || null,
      }).catch(() => {})

      // A course-preview visit also counts as a site visit for this org.
      logPageView({ orgId, path: window.location.pathname }).catch(() => {})
    }

    load()
  }, [orgId, courseId])

  const handleEnroll = async () => {
    if (!currentUser) {
      navigate("/authentication-portal")
      return
    }

    setEnrolling(true)
    setEnrollError("")

    // currentUser here is the raw Firebase Auth user (uid/email only) — our
    // custom name/avatar/nesolearnId fields live on users/{uid}.
    const identity = await getLearnerIdentity(currentUser.uid)

    const { enrollment: newEnrollment, error } = await createEnrollment({
      userId: currentUser.uid,
      courseId,
      orgId,
      enrollmentType: course?.enrollmentType,
      lessonsTotal: lessonCount,
      learnerName: identity.name,
      learnerEmail: identity.email || currentUser.email || "",
      learnerAvatarUrl: identity.avatarUrl,
      learnerNesolearnId: identity.nesolearnId,
    })

    setEnrolling(false)

    if (error) {
      setEnrollError("Something went wrong enrolling. Please try again.")
      return
    }

    setEnrollment(newEnrollment)
  }

  const moduleCount = course?.modules?.length || 0
  const lessonCount = course?.modules?.reduce((sum, m) => sum + (m?.lessons?.length || 0), 0) || 0

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading course...</span>
        </div>
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <Icon name="SearchX" size={40} className="text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Course not found</h1>
          <p className="text-muted-foreground mb-6">
            This course may be private, unpublished, or no longer available.
          </p>
          <Link to="/course-catalog">
            <Button variant="default">Back to Catalog</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{course?.title} - Nesolearn</title>
        <meta name="description" content={course?.description} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <NavigationBar />

          <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-8">
          <Link to="/course-catalog">
            <Button variant="ghost" iconName="ArrowLeft" iconPosition="left" className="mb-6">
              Back to Catalog
            </Button>
          </Link>

          {/* Header */}
          <div className="bg-card border border-border rounded-xl overflow-hidden mb-6">
            <div className="h-48 bg-muted flex items-center justify-center">
              {course?.thumbnailUrl ? (
                <img src={course?.thumbnailUrl} alt={course?.title} className="w-full h-full object-cover" />
              ) : (
                <Icon name="BookOpen" size={40} className="text-muted-foreground" />
              )}
            </div>

            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-medium text-accent uppercase tracking-wide">
                  {course?.category || "Other"}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                  {course?.enrollmentType === "open" && "Open Enrollment"}
                  {course?.enrollmentType === "invite" && "Invite Only"}
                  {course?.enrollmentType === "cohort" && "Cohort"}
                  {course?.enrollmentType === "csv" && "Bulk Enrollment"}
                </span>
              </div>

              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">{course?.title}</h1>

              <p className="text-muted-foreground mb-4">{course?.description || "No description provided."}</p>

              <div className="flex items-center gap-2 mb-4">
                {organization?.logoUrl && (
                  <img
                    src={organization?.logoUrl}
                    alt={organization?.name}
                    className="w-8 h-8 rounded-md object-cover border border-border"
                  />
                )}
                <span className="text-sm font-medium text-foreground">{organization?.name}</span>
                {organization?.verified && (
                  <span title="Verified institution">
                    <Icon name="BadgeCheck" size={16} className="text-accent" />
                  </span>
                )}
              </div>

              <div className="flex items-center gap-6 text-sm text-muted-foreground mb-6">
                <span className="flex items-center gap-1">
                  <Icon name="Layers" size={14} /> {moduleCount} modules
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="PlayCircle" size={14} /> {lessonCount} lessons
                </span>
                <span className="flex items-center gap-1 font-semibold text-foreground">
                  {course?.pricing?.isFree === false ? `${course?.pricing?.amount} NESOL` : "Free"}
                </span>
              </div>

              {enrollment ? (
                <>
                  <Button
                    variant="default"
                    size="lg"
                    fullWidth
                    iconName="PlayCircle"
                    iconPosition="left"
                    onClick={() => navigate(`/video-learning-interface?orgId=${orgId}&courseId=${courseId}`)}
                  >
                    {enrollment?.progressPercent > 0 ? "Continue Learning" : "Start Learning"}
                  </Button>
                  {course?.capstone && (
                    <Button
                      variant="outline"
                      size="lg"
                      fullWidth
                      iconName="Trophy"
                      iconPosition="left"
                      className="mt-2"
                      onClick={() => navigate(`/course-capstone?orgId=${orgId}&courseId=${courseId}`)}
                    >
                      {enrollment?.capstoneResult?.passed ? "View Capstone Result" : "Capstone Exam"}
                    </Button>
                  )}
                </>
              ) : course?.enrollmentType === "open" ? (
                <>
                  <Button
                    variant="default"
                    size="lg"
                    fullWidth
                    iconName="GraduationCap"
                    iconPosition="left"
                    loading={enrolling}
                    onClick={handleEnroll}
                  >
                    {currentUser ? "Enroll Now" : "Log In to Enroll"}
                  </Button>
                  {enrollError && (
                    <p className="text-xs text-destructive text-center mt-2">{enrollError}</p>
                  )}
                </>
              ) : (
                <>
                  <Button variant="outline" size="lg" fullWidth iconName="Mail" iconPosition="left" disabled>
                    {course?.enrollmentType === "invite" && "Invite Only"}
                    {course?.enrollmentType === "cohort" && "Cohort Enrollment"}
                    {course?.enrollmentType === "csv" && "Enrollment via Institution"}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    Contact {organization?.name || "the institution"} directly to enroll in this course.
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Curriculum */}
          <h2 className="text-lg font-bold text-foreground mb-4">Curriculum</h2>

          {moduleCount === 0 ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center">
              <Icon name="Layers" size={28} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">This course's curriculum hasn't been added yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {course?.modules?.map((module, index) => (
                <div key={module?.id} className="bg-card border border-border rounded-xl overflow-hidden">
                  <div className="px-5 py-3 border-b border-border bg-muted/30">
                    <h3 className="text-sm font-semibold text-foreground">
                      Module {index + 1}: {module?.title || "Untitled Module"}
                    </h3>
                  </div>
                  <div className="divide-y divide-border">
                    {module?.lessons?.length === 0 ? (
                      <p className="px-5 py-3 text-sm text-muted-foreground">No lessons in this module yet.</p>
                    ) : (
                      module?.lessons?.map((lesson) => (
                        <div key={lesson?.id} className="px-5 py-3 flex items-center gap-3">
                          <Icon
                            name={LESSON_TYPE_ICONS?.[lesson?.type] || "Circle"}
                            size={16}
                            className="text-muted-foreground flex-shrink-0"
                          />
                          <span className="text-sm text-foreground flex-1">
                            {lesson?.title || "Untitled Lesson"}
                          </span>
                          {lesson?.type === "video" && lesson?.duration > 0 && (
                            <span className="text-xs text-muted-foreground">{lesson?.duration} min</span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  )
}

export default CoursePreview